import { logger } from "@/lib/logger";
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSiteFeatureAccess, useSiteSubscription, formatTierPrice, useTierDefinitions } from '@/hooks/useSiteSubscription';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Lock, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SiteFeatureGateProps {
  siteId: string;
  featureName: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showUpgradePrompt?: boolean;
}

// Timeout in ms before we "fail open" and render children
const ACCESS_CHECK_TIMEOUT = 5000;

/**
 * Feature gate component that wraps components requiring specific subscription tiers
 * Shows upgrade prompt if user doesn't have access
 * 
 * IMPORTANT: This component is hardened to "fail open" - if queries fail or timeout,
 * we render children to avoid blocking users due to database issues
 */
export function SiteFeatureGate({
  siteId,
  featureName,
  children,
  fallback,
  showUpgradePrompt = true,
}: SiteFeatureGateProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: hasAccess, isLoading: accessLoading, error: accessError } = useSiteFeatureAccess(siteId, featureName);
  const { data: subscription, error: subscriptionError } = useSiteSubscription(siteId);
  const { data: tiers } = useTierDefinitions();
  
  // Timeout state - if loading takes too long, render children (fail open)
  const [timedOut, setTimedOut] = useState(false);

  // Set up timeout for access check
  useEffect(() => {
    if (accessLoading) {
      const timer = setTimeout(() => {
        logger.warn('[SiteFeatureGate] Access check timed out after', ACCESS_CHECK_TIMEOUT, 'ms, allowing access');
        setTimedOut(true);
      }, ACCESS_CHECK_TIMEOUT);
      return () => clearTimeout(timer);
    } else {
      setTimedOut(false);
    }
  }, [accessLoading]);

  // FAIL OPEN: On error, allow access and log warning
  if (accessError || subscriptionError) {
    logger.warn('[SiteFeatureGate] Query failed, allowing access:', {
      accessError: accessError?.message,
      subscriptionError: subscriptionError?.message,
      siteId,
      featureName
    });
    return <>{children}</>;
  }

  // FAIL OPEN: On timeout, allow access
  if (timedOut && accessLoading) {
    return <>{children}</>;
  }

  // Show loading state (but only briefly before timeout kicks in)
  if (accessLoading) {
    return (
      <div className="animate-pulse bg-muted/50 rounded-lg p-8 text-center">
        <p className="text-muted-foreground">{t('trialSite.components.featureGate.checkingAccess')}</p>
      </div>
    );
  }

  // If user has access, render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // If custom fallback provided, use it
  if (fallback) {
    return <>{fallback}</>;
  }

  // Show upgrade prompt if enabled
  if (showUpgradePrompt) {
    const currentTier = subscription?.tier || 'site_free';
    const requiredTier = getRequiredTierForFeature(featureName);
    const requiredTierDefinition = tiers?.find((t) => t.tier_name === requiredTier);

    return (
      <Alert className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
        <Lock className="h-5 w-5 text-primary" />
        <AlertTitle className="text-lg font-semibold">
          {t('trialSite.components.featureGate.upgradeRequired')}
        </AlertTitle>
        <AlertDescription className="mt-2 space-y-3">
          <p className="text-muted-foreground">
            {t('trialSite.components.featureGate.featureRequiresTier')}{' '}
            <span className="font-semibold text-foreground">
              {requiredTierDefinition?.display_name || requiredTier}
            </span>{' '}
            {t('trialSite.components.featureGate.subscriptionTier')}
          </p>
          
          {requiredTierDefinition && (
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-primary">
                {formatTierPrice(requiredTierDefinition.monthly_price_cents, requiredTierDefinition.currency)}
              </span>
              <span className="text-muted-foreground">{t('trialSite.components.featureGate.perMonth')}</span>
            </div>
          )}

          <p className="text-sm text-muted-foreground">
            {t('trialSite.components.featureGate.currentlyOn')}{' '}
            <span className="font-medium">{currentTier.replace('site_', '').toUpperCase()}</span> {t('trialSite.components.featureGate.tierSuffix')}
          </p>

          <Button 
            className="mt-4"
            onClick={() => navigate(`/admin/sites/${siteId}/subscription`)}
          >
            <TrendingUp className="mr-2 h-4 w-4" />
            {t('trialSite.components.featureGate.upgradeSubscription')}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Default: show nothing
  return null;
}

/**
 * Helper to determine which tier is required for a feature
 */
function getRequiredTierForFeature(featureName: string): 'site_free' | 'site_clinical' | 'site_revenue' {
  const clinicalFeatures = [
    'investigator_dashboard',
    'visit_tracking',
    'omop_sdtm_export',
    'cro_integration',
  ];

  const revenueFeatures = [
    'billing_dashboard',
    'protocol_fee_schedules',
    'invoice_generation',
    'active_trials_management',
  ];

  if (revenueFeatures.includes(featureName)) {
    return 'site_revenue';
  }

  if (clinicalFeatures.includes(featureName)) {
    return 'site_clinical';
  }

  return 'site_free';
}

/**
 * HOC version of feature gate for wrapping entire components
 */
export function withSiteFeatureGate<P extends { siteId: string }>(
  Component: React.ComponentType<P>,
  featureName: string,
  showUpgradePrompt: boolean = true
) {
  return function FeatureGatedComponent(props: P) {
    return (
      <SiteFeatureGate
        siteId={props.siteId}
        featureName={featureName}
        showUpgradePrompt={showUpgradePrompt}
      >
        <Component {...props} />
      </SiteFeatureGate>
    );
  };
}
